import SiteCoreLinks from "@/components/SiteCoreLinks";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { consolidateGraph } from "@/lib/schema-graph";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Calculator, ArrowRight, Sparkles, ShieldCheck, Clock, Languages, FileText, Phone, MessageCircle, CheckCircle2, Info } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";
const PAGE_URL = `${SITE}/quote`;
const TITLE = "คำนวณราคาแปล/รับรองเอกสารทันที — Instant Quote Calculator | NYC Language Institute";
const DESC = "เครื่องคำนวณราคาแปลเอกสาร รับรองกงสุล Notary Public NAATI ทันที พร้อมส่วนลด ระบุภาษา จำนวนหน้า ความเร่งด่วน แม่นยำ โปร่งใส — แจ้งเอกสารที่มี รู้ขั้นตอนและระยะเวลาใน 10 นาที";

export const Route = createFileRoute("/quote")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/quote", "th")},
      { name: "description", content: ctaDesc(DESC, "/quote", "th")},
      { property: "og:title", content: ctaTitle(TITLE, "/quote", "th")},
      { property: "og:description", content: ctaDesc(DESC, "/quote", "th")},
      { property: "og:url", content: PAGE_URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: ctaTitle(TITLE, "/quote", "th")},
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }, ...hreflangLinks("/quote")],
    scripts: [consolidateGraph([
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "คำนวณราคา", item: PAGE_URL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "NYC Legal Quote Calculator",
          url: PAGE_URL,
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "THB" },
        }),
      },
    ])],
  }),
  component: QuotePage,
});

type ServiceKey =
  | "translate"
  | "mfa"
  | "embassy"
  | "notary"
  | "naati"
  | "apostille"
  | "visa"
  | "company";

type LangKey = "common" | "rare" | "cjk" | "arabic";

const SERVICES: Record<ServiceKey, { label: string; base: number; unit: string; desc: string }> = {
  translate: { label: "แปลเอกสารทั่วไป", base: 500, unit: "บาท/หน้า", desc: "สูติบัตร · ทะเบียนบ้าน · ใบรับรอง" },
  mfa: { label: "รับรองกงสุล (MFA)", base: 800, unit: "บาท/ฉบับ", desc: "+ ค่าธรรมเนียมกรม 200/400 บาท" },
  embassy: { label: "รับรองสถานทูต", base: 1500, unit: "บาท/ฉบับ", desc: "+ ค่าธรรมเนียมสถานทูต (แล้วแต่ประเทศ)" },
  notary: { label: "Notary Public", base: 1500, unit: "บาท/ลายเซ็น", desc: "รับรองโดยทนายที่ขึ้นทะเบียนสภาทนาย" },
  naati: { label: "NAATI Certified (Australia)", base: 1200, unit: "บาท/หน้า", desc: "ใช้ยื่น DHA/Visa ออสเตรเลีย" },
  apostille: { label: "Apostille", base: 2500, unit: "บาท/ฉบับ", desc: "Hague Convention 130 ประเทศ" },
  visa: { label: "วีซ่า / LTR / Elite", base: 15000, unit: "บาท/เคส", desc: "เริ่มต้น ขึ้นกับประเภทวีซ่า" },
  company: { label: "จดทะเบียนบริษัท / BOI", base: 25000, unit: "บาท/เคส", desc: "รวมค่าธรรมเนียมรัฐ" },
};

const LANG_MULT: Record<LangKey, { label: string; mult: number }> = {
  common: { label: "อังกฤษ", mult: 1.0 },
  rare: { label: "ยุโรป (ฝรั่งเศส เยอรมัน สเปน อิตาลี โปรตุเกส)", mult: 1.4 },
  cjk: { label: "เอเชียตะวันออก (จีน ญี่ปุ่น เกาหลี)", mult: 1.6 },
  arabic: { label: "อาหรับ / รัสเซีย / ภาษาหายาก", mult: 1.8 },
};

const URGENCY = {
  normal: { label: "ปกติ (3-5 วัน)", mult: 1.0 },
  rush: { label: "เร่งด่วน (1-2 วัน)", mult: 1.3 },
  same: { label: "ภายในวันเดียว", mult: 1.6 },
} as const;

type UrgencyKey = keyof typeof URGENCY;

function QuotePage() {
  const [service, setService] = useState<ServiceKey>("translate");
  const [lang, setLang] = useState<LangKey>("common");
  const [pages, setPages] = useState(3);
  const [urgency, setUrgency] = useState<UrgencyKey>("normal");
  const [needMFA, setNeedMFA] = useState(false);
  const [needNotary, setNeedNotary] = useState(false);

  const isUnitDoc = service === "translate" || service === "naati";
  const isPerCase = service === "visa" || service === "company";

  const { subtotal, addons, discount, total, breakdown } = useMemo(() => {
    const s = SERVICES[service];
    const l = LANG_MULT[lang];
    const u = URGENCY[urgency];

    const qty = isPerCase ? 1 : Math.max(1, pages);
    const baseLine = s.base * qty;
    const langApplied = isUnitDoc ? baseLine * l.mult : baseLine;
    const urgencyApplied = langApplied * u.mult;

    let addOns = 0;
    const lines: { label: string; value: number }[] = [];
    lines.push({ label: `${s.label} × ${qty}`, value: baseLine });
    if (isUnitDoc && l.mult !== 1) {
      lines.push({ label: `ภาษา: ${l.label} (×${l.mult})`, value: langApplied - baseLine });
    }
    if (u.mult !== 1) {
      lines.push({ label: `ความเร่งด่วน: ${u.label} (×${u.mult})`, value: urgencyApplied - langApplied });
    }
    if (needMFA && service !== "mfa") {
      const mfa = 800 * qty + 400 * qty;
      addOns += mfa;
      lines.push({ label: `+ รับรองกงสุล MFA × ${qty}`, value: mfa });
    }
    if (needNotary && service !== "notary") {
      const n = 1500;
      addOns += n;
      lines.push({ label: `+ Notary Public`, value: n });
    }

    const sub = urgencyApplied + addOns;
    // ส่วนลดปริมาณ
    let disc = 0;
    if (isUnitDoc && qty >= 10) disc = sub * 0.1;
    else if (isUnitDoc && qty >= 5) disc = sub * 0.05;

    if (disc > 0) lines.push({ label: `ส่วนลดปริมาณ (${qty >= 10 ? "10%" : "5%"})`, value: -disc });

    return {
      subtotal: urgencyApplied,
      addons: addOns,
      discount: disc,
      total: sub - disc,
      breakdown: lines,
    };
  }, [service, lang, pages, urgency, needMFA, needNotary, isUnitDoc, isPerCase]);

  const fmt = (n: number) => `฿${Math.round(n).toLocaleString("th-TH")}`;

  const waMsg = encodeURIComponent(
    `สวัสดีครับ ขอใบเสนอราคาตามนี้:\n` +
      `• บริการ: ${SERVICES[service].label}\n` +
      (isUnitDoc ? `• ภาษา: ${LANG_MULT[lang].label}\n• จำนวน: ${pages} หน้า\n` : "") +
      `• ความเร่งด่วน: ${URGENCY[urgency].label}\n` +
      (needMFA ? `• ต้องการรับรอง MFA\n` : "") +
      (needNotary ? `• ต้องการ Notary\n` : "") +
      `• ประมาณการ: ${fmt(total)}\n` +
      `(จาก quote calculator บนเว็บ)`,
  );

  return (
    <main className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10 bg-gradient-to-br from-navy-deep via-navy-900 to-navy-deep text-white">
        <div className="absolute inset-0 opacity-30 [background:radial-gradient(circle_at_20%_20%,hsl(var(--gold)/0.25),transparent_50%),radial-gradient(circle_at_80%_60%,hsl(var(--primary)/0.2),transparent_50%)]" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gold">
            <Calculator className="h-3.5 w-3.5" /> Instant Quote Calculator
          </div>
          <h1 className="mt-4 max-w-3xl text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
            คำนวณราคา<span className="text-gold">ทันที</span><br />ก่อนตัดสินใจ
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-white/75">
            กรอกข้อมูล 4 ช่อง รู้ราคาประมาณการใน 10 วินาที โปร่งใส ไม่มีค่าใช้จ่ายแอบแฝง · ปรับตามภาษา จำนวนหน้า และความเร่งด่วน
          </p>
          <div className="mt-6 flex flex-wrap gap-4 text-sm text-white/70">
            <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-gold" /> ราคาเริ่มต้นจริง</span>
            <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-gold" /> ตอบใน 15 นาที</span>
            <span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-gold" /> ส่วนลด 5–10% ตามปริมาณ</span>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid gap-8 lg:grid-cols-5">
          {/* Inputs */}
          <div className="lg:col-span-3 space-y-8">
            {/* Service */}
            <div className="rounded-2xl border bg-card p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-bold">1. เลือกบริการ</h2>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {(Object.keys(SERVICES) as ServiceKey[]).map((k) => {
                  const s = SERVICES[k];
                  const active = service === k;
                  return (
                    <button
                      key={k}
                      onClick={() => setService(k)}
                      className={`rounded-xl border p-3 text-left transition ${
                        active
                          ? "border-primary bg-primary/5 ring-2 ring-primary/30"
                          : "border-border hover:border-primary/40 hover:bg-muted/50"
                      }`}
                    >
                      <div className="text-sm font-semibold">{s.label}</div>
                      <div className="text-xs text-muted-foreground">{s.desc}</div>
                      <div className="mt-1 text-xs font-medium text-primary">เริ่ม ฿{s.base.toLocaleString()} {s.unit}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Language */}
            {isUnitDoc && (
              <div className="rounded-2xl border bg-card p-6 shadow-sm">
                <div className="mb-4 flex items-center gap-2">
                  <Languages className="h-5 w-5 text-primary" />
                  <h2 className="text-lg font-bold">2. ภาษาปลายทาง</h2>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {(Object.keys(LANG_MULT) as LangKey[]).map((k) => {
                    const l = LANG_MULT[k];
                    const active = lang === k;
                    return (
                      <button
                        key={k}
                        onClick={() => setLang(k)}
                        className={`rounded-xl border p-3 text-left text-sm transition ${
                          active ? "border-primary bg-primary/5 ring-2 ring-primary/30" : "border-border hover:border-primary/40"
                        }`}
                      >
                        <div className="font-semibold">{l.label}</div>
                        <div className="text-xs text-muted-foreground">ตัวคูณ × {l.mult}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Pages */}
            {!isPerCase && (
              <div className="rounded-2xl border bg-card p-6 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-bold">{isUnitDoc ? "3. จำนวนหน้า" : "2. จำนวนฉบับ"}</h2>
                  <span className="text-2xl font-bold text-primary">{pages}</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={50}
                  value={pages}
                  onChange={(e) => setPages(parseInt(e.target.value))}
                  className="w-full accent-primary"
                />
                <div className="mt-2 flex justify-between text-xs text-muted-foreground">
                  <span>1</span><span>10</span><span>25</span><span>50+</span>
                </div>
                {isUnitDoc && pages >= 5 && (
                  <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-gold/15 px-2.5 py-1 text-xs font-semibold text-gold-foreground">
                    <Sparkles className="h-3 w-3" /> ปลดล็อกส่วนลด {pages >= 10 ? "10%" : "5%"}
                  </div>
                )}
              </div>
            )}

            {/* Urgency */}
            <div className="rounded-2xl border bg-card p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-bold">{isPerCase ? "3" : isUnitDoc ? "4" : "3"}. ความเร่งด่วน</h2>
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                {(Object.keys(URGENCY) as UrgencyKey[]).map((k) => {
                  const u = URGENCY[k];
                  const active = urgency === k;
                  return (
                    <button
                      key={k}
                      onClick={() => setUrgency(k)}
                      className={`rounded-xl border p-3 text-center text-sm transition ${
                        active ? "border-primary bg-primary/5 ring-2 ring-primary/30" : "border-border hover:border-primary/40"
                      }`}
                    >
                      <div className="font-semibold">{u.label}</div>
                      <div className="text-xs text-muted-foreground">× {u.mult}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Addons */}
            <div className="rounded-2xl border bg-card p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-bold">บริการเสริม (เลือกได้)</h2>
              <div className="space-y-2">
                <label className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 hover:bg-muted/50">
                  <input type="checkbox" checked={needMFA} onChange={(e) => setNeedMFA(e.target.checked)} className="h-4 w-4 accent-primary" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">+ รับรองกงสุล MFA</div>
                    <div className="text-xs text-muted-foreground">฿800 + ค่าธรรมเนียมกรม ฿400/ฉบับ</div>
                  </div>
                </label>
                <label className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 hover:bg-muted/50">
                  <input type="checkbox" checked={needNotary} onChange={(e) => setNeedNotary(e.target.checked)} className="h-4 w-4 accent-primary" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">+ Notary Public</div>
                    <div className="text-xs text-muted-foreground">฿1,500/ลายเซ็น โดยทนายขึ้นทะเบียน</div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="lg:col-span-2">
            <div className="sticky top-24 rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-card to-primary/5 p-6 shadow-xl">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">ประมาณการรวม</div>
              <div className="mt-2 text-4xl font-bold text-primary sm:text-5xl">{fmt(total)}</div>
              <div className="mt-1 text-xs text-muted-foreground">รวมภาษีและส่วนลดแล้ว (ราคาประมาณการ ราคาจริงยืนยันหลังตรวจเอกสาร)</div>

              <div className="mt-5 space-y-2 border-t pt-4">
                {breakdown.map((b, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className={b.value < 0 ? "text-gold-foreground font-medium" : "text-muted-foreground"}>{b.label}</span>
                    <span className={`font-mono ${b.value < 0 ? "text-gold-foreground font-bold" : ""}`}>{b.value < 0 ? "-" : ""}{fmt(Math.abs(b.value))}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 space-y-2">
                <a
                  href={`https://wa.me/66832494999?text=${waMsg}`}
                  target="_blank"
                  rel="noopener"
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3.5 text-sm font-bold text-primary-foreground shadow-lg transition hover:bg-primary/90"
                >
                  <MessageCircle className="h-4 w-4" /> ส่งใบเสนอราคาทาง WhatsApp
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="tel:+66832494999"
                  className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-primary bg-background px-4 py-3 text-sm font-bold text-primary transition hover:bg-primary/5"
                >
                  <Phone className="h-4 w-4" /> โทร 083-249-4999
                </a>
                <a
                  href="https://line.me/ti/p/@nycli"
                  target="_blank"
                  rel="noopener"
                  className="flex w-full items-center justify-center gap-2 rounded-xl border bg-background px-4 py-2.5 text-sm font-semibold transition hover:bg-muted"
                >
                  LINE @nycli
                </a>
              </div>

              <div className="mt-5 flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>ราคาประมาณการนี้ใช้สำหรับวางแผนเบื้องต้น ราคาจริงขึ้นกับความซับซ้อนของเอกสาร ภาษาเฉพาะทาง และค่าธรรมเนียมรัฐ เจ้าหน้าที่จะยืนยันราคาเป็นลายลักษณ์อักษรภายใน 15 นาที</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why these prices */}
      <section className="border-t bg-muted/30 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight">ทำไมราคา NYC โปร่งใส</h2>
          <p className="mt-2 max-w-2xl text-muted-foreground">เราเชื่อว่าลูกค้าควรรู้ราคาก่อนตัดสินใจ ไม่มี "ค่าเซอร์ไพรส์" ทีหลัง</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: ShieldCheck, t: "ราคาเริ่มต้นจริง", d: "ตัวเลขเดียวกับที่ลูกค้าจริงจ่าย ไม่มีค่าซ่อน" },
              { icon: Sparkles, t: "ส่วนลดอัตโนมัติ", d: "5–10% สำหรับลูกค้าที่มีปริมาณงาน" },
              { icon: Clock, t: "เร่งด่วนรู้ก่อน", d: "ค่าเร่ง × 1.3–1.6 บอกชัดในใบเสนอราคา" },
              { icon: CheckCircle2, t: "ใบกำกับภาษี VAT", d: "ออกใบกำกับภาษีถูกต้อง 100%" },
            ].map((x) => (
              <div key={x.t} className="rounded-2xl border bg-card p-5">
                <x.icon className="h-6 w-6 text-primary" />
                <div className="mt-3 text-sm font-bold">{x.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{x.d}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/pricing" className="rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground hover:bg-primary/90">ดูตารางราคาทั้งหมด</Link>
            <Link to="/compare" className="rounded-xl border-2 border-primary bg-background px-5 py-2.5 text-sm font-bold text-primary hover:bg-primary/5">เปรียบเทียบกับเจ้าอื่น</Link>
            <Link to="/case-studies" className="rounded-xl border bg-background px-5 py-2.5 text-sm font-semibold hover:bg-muted">ดู Case Studies</Link>
          </div>
        </div>
      </section>
          <SiteCoreLinks />
    </main>
  );
}
